'use client';

import Image from '@/components/Image';
import { Product } from '@/components/Product/ProductCard';
import PriceComponent from '@/components/Product/Price';
import { IVipPrice } from '@/api/types/public';
import { useTranslations } from '@/i18n/use-translations';
import { useJump } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useCallback } from 'react';

function isSoldOut(product: Product) {
  return (
    product.status === 2 ||
    product.stock <= 0 ||
    (product as { in_stock?: string }).in_stock === '2'
  );
}

export default function ProductCard({
  product,
  isWeb,
  index,
  handleProductClick,
  handleProductValidInView,
}: {
  product: Product;
  isWeb: boolean;
  index: number;
  handleProductClick: (index: number, item: Product) => void;
  handleProductValidInView: (index: number, item: Product) => void;
}) {
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const t = useTranslations('product');
  const soldOut = isSoldOut(product);
  const noRestock = product.status === 2;
  const soldOutText = noRestock ? t('soldOut.noRestock') : t('soldOut.soldOut');
  const toJumpTarget = useJump();

  const { ref: validExposureRef } = useValidExposure({
    callback: useCallback(() => {
      handleProductValidInView(index, product);
    }, [index, product, handleProductValidInView]),
    ts: product.shop_sku,
  });

  const jumpToGoodsDetail = useCallback(() => {
    handleProductClick(index, product);
    setTimeout(() => {
      toJumpTarget({
        jump_type: 4,
        Jump_target: `/goods-detail/${product.shop_sku}`,
      });
    });
  }, [product, index, handleProductClick, toJumpTarget]);

  return (
    <a
      ref={validExposureRef}
      href={`/goods-detail/${product.shop_sku}`}
      onClick={(e) => {
        e.preventDefault();
        jumpToGoodsDetail();
      }}
      className={`relative block aspect-[1/1] bg-[#eeeeee] rounded-[8px] overflow-hidden cursor-pointer ${isWeb ? '' : 'w-[160px] h-[160px]'}`}
    >
      <Image
        className={`mx-auto ${isWeb ? 'mt-[26.845638%] w-[82.1%]' : 'mt-[20px] w-[120px] h-[60px]'} ${product.image?.product_gray?.[0] ? '' : 'mix-blend-multiply'} ${soldOut ? 'opacity-50' : ''}`}
        src={
          product.image?.product_gray?.[0] ||
          product.image?.product?.[0] ||
          product.image?.material?.[0] ||
          product.image?.celebrity?.[0]
        }
        alt={product.name}
        width={367}
        height={184}
      />
      {soldOut && (
        <div
          className={`absolute left-1/2 flex aspect-square translate-x-[-50%] items-center justify-center rounded-[50%] bg-[#00000099] text-center leading-[11px] ${isWeb ? 'h-[30%] top-[32%]' : 'h-[66px] w-[66px] top-[20px]'}`}
        >
          <div
            className={`whitespace-pre-wrap text-primary-white ${isWeb ? 'text-14 leading-[20px]' : 'text-11 leading-[20px]'}`}
            dangerouslySetInnerHTML={{ __html: soldOutText }}
          />
        </div>
      )}
      <div
        className={`px-[5.369%] bottom-0 absolute w-full box-border ${isWeb ? 'pb-[5.369%]' : 'pb-[20px]'}`}
      >
        <p
          className={`text-[#808080] font-br w-full ${isWeb ? 'text-[16px] leading-[19px]' : 'text-center text-[12px] leading-[14px]'}`}
        >
          {product.short_name || product.name}
        </p>
        <PriceComponent
          priceObject={product as IVipPrice}
          renderPrice={(price, sellPrice) => (
            <div className={`flex items-center ${isWeb ? 'mt-[12px]' : 'mt-[4px] justify-center'}`}>
              <span
                className={`notranslate font-bb text-[#282828] ${isWeb ? 'leading-[34px] text-[28px]' : 'leading-[19px] text-[16px]'}`}
                aria-label={tPriceA11y('srCurrent') + sellPrice}
              >
                {sellPrice}
              </span>
              {price && (
                <span
                  className={`line-through text-[#808080] notranslate ${isWeb ? 'text-[16px] leading-[34px] ml-[12px]' : 'ml-[8px] text-[12px] leading-[14px]'}`}
                >
                  <del aria-label={tPriceA11y('srOriginal') + price}>{price}</del>
                </span>
              )}
            </div>
          )}
        />
      </div>
    </a>
  );
}
